import {
  ApiTeamsGet200ResponseInnerSubscriptionTier,
  SubscriptionInfo,
  TierInfo,
  TierUsageInfo,
} from '../client';

/** The marketing pricing page writes this; the plan picker consumes it. */
export const PRICING_PLAN_STORAGE_KEY = 'delegate.pricing.plan';

/**
 * Fired on window after any subscription mutation (subscribe, pay-now,
 * scheduled-change edits) so passive views — the team status banner — refetch
 * immediately instead of waiting for the next navigation.
 */
export const TEAM_SUBSCRIPTION_CHANGED_EVENT =
  'delegate:team-subscription-changed';

export function notifyTeamSubscriptionChanged(): void {
  window.dispatchEvent(new Event(TEAM_SUBSCRIPTION_CHANGED_EVENT));
}

export const PLAN_STATUS_LABELS: Record<SubscriptionInfo['status'], string> = {
  trialing: 'Trial',
  active: 'Active',
  past_due: 'Past due',
  paused: 'Paused',
  trial_expired: 'Trial ended',
  canceled: 'Canceled',
};

export function planDaysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

export function planShortDate(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleDateString() : '—';
}

export function planIsBlocked(status: SubscriptionInfo['status']): boolean {
  return (
    status === 'paused' || status === 'trial_expired' || status === 'canceled'
  );
}

export function planErrorText(error: unknown, fallback: string): string {
  const maybeAxios = error as {
    response?: {
      data?: { error?: string; violations?: { message: string }[] };
    };
  };
  const data = maybeAxios.response?.data;
  if (data?.violations?.length) {
    return data.violations.map((violation) => violation.message).join(' ');
  }
  return data?.error ?? fallback;
}

/**
 * The plan's limits and switches as they ride the teams payload. Every
 * member gets this regardless of billing permission, which is what lets a
 * view refuse a creation the plan excludes before the server has to.
 */
export type PlanTier = ApiTeamsGet200ResponseInnerSubscriptionTier;

/** A team-shaped object carrying the subscription the teams list returns. */
export interface TeamWithPlan {
  subscription?: { tier?: PlanTier | null } | null;
}

export type PlanFeature =
  | 'allowByoModelKeys'
  | 'allowCustomPermissionRoles'
  | 'allowCustomAgentRoles'
  | 'allowCustomSkills'
  | 'allowCustomTools';

export function tierOfTeam(
  team: TeamWithPlan | null | undefined,
): PlanTier | null {
  return team?.subscription?.tier ?? null;
}

/**
 * True when the plan excludes `feature`. An absent tier (pre-migration data,
 * or a payload that predates this field) permits everything: the server is
 * the authority, and a missing plan must never lock the product.
 */
export function tierBlocksFeature(
  tier: PlanTier | null | undefined,
  feature: PlanFeature,
): boolean {
  if (!tier) return false;
  return tier[feature] === false;
}

/** True when `current` has reached a non-null tier cap. */
export function tierAtLimit(
  limit: number | null | undefined,
  current: number,
): boolean {
  return limit != null && current >= limit;
}

/**
 * Which usage count answers each tier column, and how to name it to a human.
 * Every entry is keyed by the tier's own column, so a plan's behaviour follows
 * the values on its row: adding a tier is a row, and adding a restriction is a
 * column plus one line here.
 */
const FEATURE_USAGE: ReadonlyArray<{
  feature: PlanFeature;
  usageKey: keyof TierUsageInfo;
  noun: string;
}> = [
  {
    feature: 'allowByoModelKeys',
    usageKey: 'modelProviders',
    noun: 'model provider',
  },
  {
    feature: 'allowCustomPermissionRoles',
    usageKey: 'customPermissionRoles',
    noun: 'custom permission role',
  },
  {
    feature: 'allowCustomSkills',
    usageKey: 'customSkills',
    noun: 'custom skill',
  },
  { feature: 'allowCustomTools', usageKey: 'customTools', noun: 'custom tool' },
];

const COUNT_LIMITS: ReadonlyArray<{
  limit: keyof TierInfo;
  usageKey: keyof TierUsageInfo;
  noun: string;
}> = [
  { limit: 'maxAgents', usageKey: 'agents', noun: 'agent' },
  { limit: 'maxUsers', usageKey: 'billableUsers', noun: 'billable user' },
  { limit: 'maxViewers', usageKey: 'viewers', noun: 'free viewer' },
  { limit: 'maxProjects', usageKey: 'projects', noun: 'project' },
];

export interface TierFeatureLoss {
  feature: PlanFeature;
  /** How much of the capability the team holds today. */
  current: number;
  noun: string;
}

export interface TierCountOverage {
  limit: keyof TierInfo;
  current: number;
  allowed: number;
  noun: string;
}

/**
 * What choosing `tier` would change for a team holding `usage`: capability it
 * would lose, and counts it already exceeds. Both halves are read off the
 * tier's columns rather than any notion of which tier this is.
 *
 * Without a usage snapshot the answer is "nothing known" rather than
 * "nothing changes" — the card stays quiet instead of promising a clean switch
 * it cannot verify.
 */
export function tierChangesOnSwitch(
  tier: TierInfo,
  usage: TierUsageInfo | null | undefined,
): { losses: TierFeatureLoss[]; overage: TierCountOverage[] } {
  if (!usage) return { losses: [], overage: [] };

  const losses: TierFeatureLoss[] = [];
  for (const entry of FEATURE_USAGE) {
    const current = Number(usage[entry.usageKey] ?? 0);
    if (tier[entry.feature] === false && current > 0) {
      losses.push({ feature: entry.feature, current, noun: entry.noun });
    }
  }

  const overage: TierCountOverage[] = [];
  for (const entry of COUNT_LIMITS) {
    const allowed = tier[entry.limit] as number | null | undefined;
    if (allowed == null) continue;
    const current = Number(usage[entry.usageKey] ?? 0);
    if (current > allowed) {
      overage.push({ limit: entry.limit, current, allowed, noun: entry.noun });
    }
  }

  return { losses, overage };
}

/** "2 model providers" / "1 custom skill". */
export function planCountLabel(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

/** The server's refusal copy, rendered before the server has to send it. */
export function planFeatureUpgradeText(
  featureLabel: string,
  tierName: string,
): string {
  return `${featureLabel} is not included in the ${tierName} plan. Upgrade the plan to use it.`;
}

/** Peek at the pricing-page handoff without consuming it. */
export function peekPricingPlanHint(): string | null {
  try {
    return window.localStorage.getItem(PRICING_PLAN_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Consume the pricing-page handoff (the picker page clears it). */
export function consumePricingPlanHint(): string | null {
  try {
    const stored = window.localStorage.getItem(PRICING_PLAN_STORAGE_KEY);
    if (stored) {
      window.localStorage.removeItem(PRICING_PLAN_STORAGE_KEY);
    }
    return stored;
  } catch {
    return null;
  }
}
